import { useQuery } from "@tanstack/react-query";
import { menuCardLabels, productNameWithoutUnit } from "@shared/productNames";
import type { PickSheetSettings } from "@shared/schema";
import { UnitPill } from "@/components/unit-pill";
import { getApiPath } from "@/lib/queryClient";

/** One product as the pictures draw it: its parts for the menu, its whole name for the sheet. */
export type PreviewMember = {
  id: number | string;
  /** The official name: the item and pack joined. */
  name: string;
  item: string;
  pack: string;
  unit?: string | null;
  price?: string | null;
  subheading?: string | null;
};

/**
 * The product form's live picture of what is being typed, in the two places it shows:
 * the card on the customer's menu (size-group-card.tsx, or the single card — the item
 * heads it, then each unit is a tag and a price, an old pack standing in the unit's
 * place: menuCardLabels) and the lines on this org's pick sheet (server/pickSheet.ts —
 * each whole name, which invoices and the orders page print too; with the org's Unit
 * column on, the name without its unit, as the sheet prints it). `members` is the
 * product alone, or its size group with the lead first.
 */
export function ProductNamePreview({ members, photo }: { members: PreviewMember[]; photo?: string | null }) {
  // The pick sheet card's own query (same key), so the picture has the columns this org prints.
  const { data: pickSheet } = useQuery<PickSheetSettings>({ queryKey: [getApiPath("/api/pick-sheet-settings")] });
  if (members.length === 0) return null;
  const named = members.map((member) => ({ ...member, itemName: member.item }));
  const [labels] = menuCardLabels([{ key: 0, head: named[0], members: named }]);
  const single = members.length === 1;
  const sheetCell = "border border-gray-400 px-1.5 py-0.5";
  const stepper = (
    <span aria-hidden className="flex flex-shrink-0 items-center gap-0.5 text-[10px] font-medium">
      <span className="rounded bg-[hsl(140,27%,88%)] px-1.5 py-0.5 text-cn-brand-green">−</span>
      <span className="rounded bg-gray-50 px-1.5 py-0.5 text-gray-400">0</span>
      <span className="rounded bg-[hsl(140,27%,88%)] px-1.5 py-0.5 text-cn-brand-green">+</span>
    </span>
  );
  return (
    <div className="space-y-4" data-testid="product-name-preview">
      <figure className="min-w-0">
        <figcaption className="mb-1 text-xs text-gray-500">Customer's menu</figcaption>
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-start gap-2.5">
            <div className="h-11 w-11 flex-shrink-0 overflow-hidden rounded bg-gray-100">
              {photo && <img src={photo} alt="" className="h-full w-full object-cover" loading="lazy" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="break-words text-sm font-medium text-gray-900" data-testid="text-preview-title">
                {labels.title || <span className="font-normal text-gray-400">Item</span>}
                {single && <UnitPill unit={labels.tags[0]} className="px-1.5 text-[10px]" />}
              </p>
              {members[0].subheading && <p className="text-xs font-light text-gray-600">{members[0].subheading}</p>}
            </div>
            {single && members[0].price && (
              <span className="flex-shrink-0 text-xs font-medium tabular-nums text-gray-700">${members[0].price}</span>
            )}
          </div>
          {single ? (
            <div
              aria-hidden
              className="mt-2 rounded-lg bg-[hsl(140,27%,88%)] py-1 text-center text-xs font-medium text-cn-brand-green"
            >
              Add
            </div>
          ) : (
            <ul className="mt-2 border-t border-gray-100">
              {members.map((member, index) => {
                const tag = labels.tags[index];
                return (
                  <li
                    key={member.id}
                    className="flex items-center justify-between gap-1.5 border-b border-gray-100 py-1.5 text-xs text-gray-700 last:border-b-0 last:pb-0"
                    data-testid={`row-preview-${member.id}`}
                  >
                    <span className="min-w-0 break-words">
                      {tag ? <UnitPill unit={tag} className="ml-0 whitespace-normal px-1.5 text-[10px]" /> : member.name}
                    </span>
                    <span className="flex flex-shrink-0 items-center gap-1.5">
                      {member.price && <span className="tabular-nums">${member.price}</span>}
                      {stepper}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </figure>
      <figure className="min-w-0">
        <figcaption className="mb-1 text-xs text-gray-500">Pick sheet, invoice, orders page</figcaption>
        <table className="w-full border-collapse bg-white text-xs text-gray-900 shadow-sm">
          <thead>
            <tr>
              <th className={`${sheetCell} w-px font-semibold`}>Qty</th>
              {pickSheet?.showUnitColumn && <th className={`${sheetCell} w-px font-semibold`}>Unit</th>}
              <th className={`${sheetCell} text-left font-semibold`}>Item</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id}>
                <td className={`${sheetCell} text-center`}>1</td>
                {pickSheet?.showUnitColumn && <td className={`${sheetCell} whitespace-nowrap text-center`}>{member.unit}</td>}
                <td className={`${sheetCell} break-words`} data-testid={`text-preview-name-${member.id}`}>
                  {pickSheet?.showUnitColumn
                    ? productNameWithoutUnit({ name: member.name, itemName: member.item, pack: member.pack, unit: member.unit })
                    : member.name}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </figure>
    </div>
  );
}
